import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/contexts/AuthContext';
import styles from '@/components/auth/AuthPages.module.css';
import securityStyles from './SecurityPage.module.css';
import { routes } from '@/lib/routePaths';

function PasswordForm({ forced }: { forced: boolean }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { changePassword, logout } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 12) {
      setError('The new password must be at least 12 characters.');
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      navigate(forced ? routes.overview : routes.security, { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to change the password.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card header={forced ? 'Change your temporary password' : 'Change password'}>
      <form className={styles.form} onSubmit={handleSubmit}>
            {error && <Alert variant="danger">{error}</Alert>}
            {forced && (
              <p className={styles.hint}>Choose a new password before continuing to the workspace.</p>
            )}
            <Input
              label="Current password"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
            <Input
              label="New password"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
            <Input
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
            <div className={styles.actions}>
              {forced ? (
                <Button type="button" variant="ghost" onClick={() => void logout()}>Sign out</Button>
              ) : (
                <Button type="button" variant="ghost" onClick={() => navigate(-1)}>Cancel</Button>
              )}
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Changing...' : 'Change password'}
              </Button>
            </div>
      </form>
    </Card>
  );
}

export default function ChangePasswordPage() {
  const { user } = useAuth();
  if (!user?.mustChangePassword) return <Navigate to={routes.security} replace />;
  return <main className={styles.authPage}><div className={styles.authContent}>
    <div className={styles.brand}>MegaTraderDX</div>
    <PasswordForm forced />
  </div></main>;
}

export function SecurityPage() {
  return (
    <section className={securityStyles.page}>
      <header>
        <h1>Security</h1>
        <p className={styles.hint}>Update the password used to sign in to your account.</p>
      </header>
      <PasswordForm forced={false} />
    </section>
  );
}
